
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import SphereViewerDialog from '@/components/SphereViewerDialog.vue'
import DrawerCard from '@/components/DrawerCard.vue'
import Icon from '@/components/Icon.vue'
import { Panorama } from '@/models/Panorama'
import { GeoJSON } from 'geojson'
import { bearing } from '@turf/bearing'
import { distance } from '@turf/distance'
import axios from 'axios'

@Component({
	components: {
		SphereViewerDialog,
		DrawerCard,
		Icon
	}
})
export default class PanoramaPreview extends Vue {
	@Prop({ required: true })
	panoramas!: Array<Panorama>

	@Prop({ required: true })
	bearingTo!: GeoJSON.Point

	isLoading = true
	previewImageBlob: Blob | null = null
	lastPreviewURL: string | null = null

	URL = URL

	/**
	 * Direction of the facility relative to the centre of the panorama image (its cog),
	 * in 0-360. This is the frame the sphere viewer takes, so it is what the click emits.
	 */
	get bearing(): number {
		if (!this.panorama) return 0
		const relative =
			bearing((this.panorama!.position as GeoJSON.Point).coordinates, this.bearingTo.coordinates) -
			(this.panorama!.cog ?? 0)
		return ((relative % 360) + 360) % 360
	}

	/**
	 * The strip's background is anchored by the image's left edge: `--elem-center-offset`
	 * puts texture x=0, which is the direction opposite to cog, at the centre of the
	 * element. So the strip needs the bearing half a turn from the viewer's. This used to
	 * be folded into `bearing` itself and leaked into the emitted value (BGT-7790).
	 */
	get stripBearing(): number {
		const imageRearHeadingFlip = 180
		return (this.bearing + imageRearHeadingFlip) % 360
	}

	get panorama(): Panorama | null {
		const facilityCoordinates = this.bearingTo.coordinates
		const sortedByDistance = this.panoramas.sort((a, b) =>
			distance(facilityCoordinates, (a.position as GeoJSON.Point).coordinates) >
			distance(facilityCoordinates, (b.position as GeoJSON.Point).coordinates)
				? 1
				: -1
		)
		const nearest = sortedByDistance.filter((panorama) => panorama.is360Image)[0]

		// 600m is the distance threshold
		if (nearest && distance(facilityCoordinates, (nearest.position as GeoJSON.Point).coordinates) < 0.6) {
			return nearest
		}

		return sortedByDistance
			.filter((p) => !p.is360Image)
			.filter(
				(p) =>
					Math.abs(
						(p.cog ?? 0) - bearing((p!.position as GeoJSON.Point).coordinates, this.bearingTo.coordinates)
					) < 70 // avg camera FOV
			)[0]
	}

	@Watch('panorama', { immediate: true })
	async onPanoramaChange() {
		if (this.lastPreviewURL === this.panorama?.smallImageURL) {
			return
		}
		try {
			if (this.panorama?.smallImageURL) {
				const img = await axios.get(this.panorama?.smallImageURL, {
					responseType: 'blob'
				})

				this.previewImageBlob = img.data
				this.lastPreviewURL = this.panorama?.smallImageURL
			} else {
				this.previewImageBlob = null
				this.lastPreviewURL = null
			}
		} catch {
			this.previewImageBlob = null
			this.lastPreviewURL = null
		} finally {
			this.isLoading = false
		}
	}
}
